import { useMemo } from "react";
import clsx from "clsx";

import { Receipt } from "@chef/components";
import { language } from "@chef/constants";
import {
  addTimeToDate,
  formatDate,
  isFinancialProduct,
  isMealboxProduct,
  isPickAndMixProduct,
} from "@chef/helpers";
import { OrderQuery, useOrderQuery } from "@chef/state-management";

import { OrderTrackingLink } from "../OrderTrackingLink";
import { ShoppingCart } from "../shopping-cart/ShoppingCart";

import { intl } from "./OrderReceipt.Intl";

interface OrderReceiptProps {
  orderId: OrderQuery["order"]["orderId"];
  className?: string;
}

export const OrderReceipt = ({ orderId, className }: OrderReceiptProps) => {
  const { data: orderQuery } = useOrderQuery({
    orderId,
  });

  const order = orderQuery?.order;

  const isDelivered = useMemo(() => {
    if (!order) {
      return false;
    }

    const now = new Date();
    let date = new Date(order.deliveryDate);

    const timeblock = order.deliveryAddress.addressData.timeblocks.find(
      (tb) => tb.timeblockId === order.timeblockId,
    );
    if (timeblock) {
      const [hours, minutes, seconds] = timeblock.to.split(":").map((s) => +s);

      date = addTimeToDate(date, { hours, minutes, seconds });
    }

    return now > date;
  }, [order]);

  if (!order) {
    return;
  }

  const primaryProduct = order.orderLines.find(
    (ol) =>
      isMealboxProduct(ol.variation.product) ||
      isFinancialProduct(ol.variation.product),
  );

  const pickAndMixProduct = order.orderLines.find((ol) =>
    isPickAndMixProduct(ol.variation.product),
  );

  const deliveryDateFormatted = formatDate(
    order.deliveryDate,
    intl.DATE_FORMAT,
  );

  const deliveryAddress = order.deliveryAddress.addressData.street;

  if (!primaryProduct && !pickAndMixProduct) {
    return null;
  }

  return (
    <Receipt
      className={clsx("flex flex-col gap-4", className)}
      hideDetailsButton
    >
      <Receipt.Title as="h2" language={language} />

      <Receipt.Header language={language}>
        {intl.DELIVERED_AT_TO(
          deliveryDateFormatted,
          deliveryAddress,
          isDelivered,
        )}
        <OrderTrackingLink order={order} />
      </Receipt.Header>

      {order.paymentData?.invoiceNumber && (
        <Receipt.DownloadIconButton
          href={`/api/payment/downloadReceipt?receiptId=${order.paymentData.invoiceNumber}`}
          download={`Kvitto ${formatDate(order.deliveryDate, "yyyy-MM-dd")}`}
          language={language}
        />
      )}

      <div>
        <strong>{intl.DETAILED_OVERVIEW}</strong>

        <ShoppingCart order={order} />
      </div>
    </Receipt>
  );
};
